import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, MessageSquare, Send, IndianRupee, ArrowLeft, Paperclip, Flag, Ban, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime, fileUrl } from "../../lib/api";
import { PageHeader } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function Messages() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(uid || "");
  const [msgs, setMsgs] = useState([]);
  const [offers, setOffers] = useState([]);
  const [text, setText] = useState("");
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offer, setOffer] = useState({ description: "", price: "", delivery_days: 3, revisions: 1 });
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const loadThreads = () => api.get("/mkt/messages").then((r) => setThreads(r.data.threads)).catch(() => {});
  const loadMsgs = (other) => other && api.get(`/mkt/messages/${other}`).then((r) => setMsgs(r.data.messages)).catch(() => {});
  const loadOffers = () => api.get("/mkt/offers").then((r) => setOffers(r.data.offers)).catch(() => {});

  useEffect(() => {
    loadThreads(); loadOffers();
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/mkt/freelancer/me").then((r) => setMe((m) => ({ ...m, _fl: r.data.active }))).catch(() => {});
  }, []);
  useEffect(() => { if (uid) setActive(uid); }, [uid]);
  useEffect(() => {
    loadMsgs(active);
    const t = setInterval(() => { loadMsgs(active); loadThreads(); loadOffers(); }, 5000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length, offers.length]);

  const activeThread = threads.find((t) => t.user_id === active);
  const myId = me?.id || me?._id;

  const send = async (attachment) => {
    if ((!text.trim() && !attachment) || !active) return;
    setBusy(true);
    try {
      await api.post("/mkt/messages", { to_user_id: active, text: text.trim(), attachment });
      setText(""); loadMsgs(active); loadThreads();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const attach = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads", fd);
      await send({ path: data.path, name: f.name });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };

  const sendOffer = async () => {
    if (!offer.description.trim() || !offer.price) { toast.error("Description and price are required"); return; }
    setBusy(true);
    try {
      await api.post("/mkt/offers", { to_user_id: active, ...offer });
      toast.success("Custom offer sent"); setOfferOpen(false); setOffer({ description: "", price: "", delivery_days: 3, revisions: 1 }); loadOffers();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const acceptOffer = async (of) => {
    setBusy(true);
    try { await api.post(`/mkt/offers/${of.id}/accept`); toast.success("Offer accepted — complete payment in My Orders"); navigate("/mkt/orders"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const declineOffer = async (of) => {
    try { await api.post(`/mkt/offers/${of.id}/decline`); toast.success("Offer declined"); loadOffers(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const report = async () => {
    if (!reportReason.trim()) { toast.error("Reason required"); return; }
    setBusy(true);
    try { await api.post("/mkt/report", { user_id: active, reason: reportReason }); toast.success("Reported — our team will review"); setReportOpen(false); setReportReason(""); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const toggleBlock = async () => {
    try {
      if (activeThread?.blocked) { await api.delete(`/mkt/block/${active}`); toast.success("User unblocked"); }
      else { await api.post(`/mkt/block/${active}`); toast.success("User blocked"); }
      loadThreads();
    } catch (e) { toast.error(errMsg(e)); }
  };

  // merge messages + this-thread offers into one feed
  const feed = [
    ...msgs.map((m) => ({ ...m, _t: "msg" })),
    ...offers.filter((o) => (o.buyer_id === active && o.freelancer_id === myId) || (o.freelancer_id === active && o.buyer_id === myId))
      .map((o) => ({ ...o, _t: "offer" })),
  ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  return (
    <div className="space-y-2" data-testid="messages-page">
      <PageHeader title="Messages" subtitle="Secure in-platform chat — no external apps needed" testid="messages-title" />
      <div className="grid md:grid-cols-[300px_1fr] gap-4 h-[calc(100vh-220px)] min-h-[480px]">
        <div className={`bg-card border border-border rounded-xl overflow-y-auto ${active ? "hidden md:block" : ""}`} data-testid="thread-list">
          {threads.length === 0 && (
            <div className="p-8 text-center">
              <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-bold">No conversations</p>
              <p className="text-xs text-muted-foreground mt-1">Open any marketplace listing and tap Chat to start.</p>
            </div>)}
          {threads.map((t) => (
            <button key={t.user_id} data-testid={`thread-${t.user_id}`} onClick={() => { setActive(t.user_id); navigate(`/mkt/messages/${t.user_id}`, { replace: true }); }}
              className={`w-full text-left px-4 py-3.5 border-b border-border/50 last:border-0 hover:bg-muted/50 transition-colors ${active === t.user_id ? "bg-muted" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-bold text-sm truncate">{t.name} {t.is_freelancer && <span className="text-[9px] font-extrabold text-primary uppercase">· Seller</span>}</p>
                {t.unread > 0 && <span className="h-5 min-w-5 px-1 rounded-full gradient-bg text-white text-[10px] font-bold flex items-center justify-center shrink-0">{t.unread}</span>}
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">{t.last}</p>
            </button>
          ))}
        </div>

        <div className="bg-card border border-border rounded-xl flex flex-col overflow-hidden" data-testid="conversation-panel">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Select a conversation</div>
          ) : (
            <>
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
                <button className="md:hidden" onClick={() => { setActive(""); navigate("/mkt/messages", { replace: true }); }}><ArrowLeft className="h-4 w-4" /></button>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate" data-testid="active-thread-name">{activeThread?.name || "Conversation"}</p>
                  {activeThread?.slug && <button onClick={() => navigate(`/marketplace/f/${activeThread.slug}`)} className="text-[11px] text-primary font-semibold">View profile →</button>}
                </div>
                {me?._fl && (
                  <Button size="sm" variant="outline" data-testid="send-offer-btn" onClick={() => setOfferOpen(true)} className="rounded-full font-bold shrink-0">
                    <IndianRupee className="h-3.5 w-3.5 mr-1" />Send Offer</Button>)}
                <button data-testid="report-user-btn" onClick={() => setReportOpen(true)} title="Report user"
                  className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-amber-500 transition-colors shrink-0"><Flag className="h-3.5 w-3.5" /></button>
                <button data-testid="block-user-btn" onClick={toggleBlock} title={activeThread?.blocked ? "Unblock user" : "Block user"}
                  className={`h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors shrink-0 ${activeThread?.blocked ? "text-red-500" : "text-muted-foreground hover:text-red-500"}`}><Ban className="h-3.5 w-3.5" /></button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {activeThread?.blocked && <p className="text-center text-xs text-red-500 font-semibold py-2" data-testid="blocked-note">You blocked this user — messages are disabled.</p>}
                {feed.map((m) => m._t === "offer" ? (
                  <div key={`of-${m.id}`} className={`flex ${m.freelancer_id === myId ? "justify-end" : "justify-start"}`}>
                    <div data-testid={`chat-offer-${m.id}`} className="max-w-[80%] rounded-2xl border-2 border-primary/30 bg-primary/5 p-4 space-y-2">
                      <p className="text-[10px] font-extrabold uppercase tracking-wider text-primary">Custom Offer</p>
                      <p className="text-sm">{m.description}</p>
                      <p className="font-display font-extrabold text-xl">{fmtINR(m.price)}</p>
                      <p className="text-[11px] text-muted-foreground">{m.delivery_days}d delivery · {m.revisions} revision{m.revisions === 1 ? "" : "s"} · status: {m.status}</p>
                      {m.status === "open" && m.buyer_id === myId && (
                        <div className="flex gap-2">
                          <Button size="sm" data-testid={`chat-offer-accept-${m.id}`} onClick={() => acceptOffer(m)} disabled={busy} className="rounded-full font-bold gradient-bg text-white border-0"><Check className="h-3.5 w-3.5 mr-1" />Accept & Pay</Button>
                          <Button size="sm" variant="outline" data-testid={`chat-offer-decline-${m.id}`} onClick={() => declineOffer(m)} className="rounded-full font-bold"><X className="h-3.5 w-3.5 mr-1" />Decline</Button>
                        </div>)}
                    </div>
                  </div>
                ) : (
                  <div key={m.id} className={`flex ${m.from_id === myId ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${m.from_id === myId ? "gradient-bg text-white rounded-br-sm" : "bg-muted rounded-bl-sm"}`} data-testid={`msg-${m.id}`}>
                      {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                      {m.attachment && (
                        <a href={fileUrl(`/api/files/${m.attachment.path}`)} target="_blank" rel="noreferrer" data-testid={`msg-att-${m.id}`}
                          className={`flex items-center gap-1.5 text-xs font-bold mt-1 underline ${m.from_id === myId ? "text-white" : "text-primary"}`}>
                          <Paperclip className="h-3 w-3" />{m.attachment.name}
                        </a>)}
                      <p className={`text-[10px] mt-1 ${m.from_id === myId ? "text-white/70" : "text-muted-foreground"}`}>{fmtDateTime(m.created_at)}</p>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              <div className="p-3 border-t border-border flex gap-2">
                <input ref={fileRef} type="file" className="hidden" onChange={attach} />
                <Button variant="ghost" size="icon" data-testid="message-attach" onClick={() => fileRef.current?.click()} disabled={busy || activeThread?.blocked} className="rounded-full shrink-0">
                  <Paperclip className="h-4 w-4" />
                </Button>
                <Input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} disabled={activeThread?.blocked}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                  placeholder={activeThread?.blocked ? "Blocked" : "Type a message… (contact/payment info is blocked for safety)"} className="rounded-full" />
                <Button data-testid="message-send" onClick={() => send()} disabled={busy || (!text.trim()) || activeThread?.blocked} className="rounded-full gradient-bg text-white border-0 shrink-0">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
        <DialogContent data-testid="offer-dialog">
          <DialogHeader><DialogTitle className="font-display">Send Custom Offer</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>What will you deliver?</Label><Textarea data-testid="offer-description" value={offer.description} onChange={(e) => setOffer({ ...offer, description: e.target.value })} rows={3} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Price (₹)</Label><Input data-testid="offer-price" type="number" min="1" value={offer.price} onChange={(e) => setOffer({ ...offer, price: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Days</Label><Input data-testid="offer-days" type="number" min="1" value={offer.delivery_days} onChange={(e) => setOffer({ ...offer, delivery_days: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Revisions</Label><Input data-testid="offer-revisions" type="number" min="0" value={offer.revisions} onChange={(e) => setOffer({ ...offer, revisions: e.target.value })} /></div>
            </div>
            <Button data-testid="offer-submit" onClick={sendOffer} disabled={busy} className="w-full rounded-full font-bold gradient-bg text-white border-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Send Offer ${offer.price ? `· ${fmtINR(offer.price)}` : ""}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader><DialogTitle className="font-display">Report User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Reason</Label><Textarea data-testid="report-reason" value={reportReason} onChange={(e) => setReportReason(e.target.value)} rows={3} placeholder="Spam, abuse, fraud attempt…" /></div>
            <Button data-testid="report-submit" onClick={report} disabled={busy} variant="destructive" className="w-full rounded-full font-bold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Report"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
